import { useEffect, useRef } from "react";
import { PathPattern, useMatch } from "react-router";

export const DEFAULT_TITLE = "Member 360";

type Pattern = PathPattern<string> | string;

function resolvePageTitle(title?: string | null): string | null {
  if (title === null) {
    return null;
  }

  if (title === undefined) {
    return DEFAULT_TITLE;
  }

  if (!title || !title.trim()) {
    return DEFAULT_TITLE;
  }

  return title;
}

/**
 * Hook for managing page titles with support for loading states and automatic fallbacks.
 *
 * This hook sets the document title based on the current route pattern and provided title.
 * It's designed to handle async data loading gracefully by preserving the current title
 * during loading states.
 *
 * @param pattern - The route pattern to match against current location
 * @param title - The title to set, with special handling for different states:
 *   - `null`: Preserves current title (use during loading states)
 *   - `undefined`: Sets to DEFAULT_TITLE fallback
 *   - `""` or whitespace: Sets to DEFAULT_TITLE fallback
 *   - Valid string: Sets to that title
 *
 * @returns A function to dynamically update the title
 */
export default function usePageTitle(pattern: Pattern, title?: string | null) {
  const match = useMatch(pattern);
  const entryTitleRef = useRef<string | null>(null);
  const lastSetTitleRef = useRef<string | null>(null);

  useEffect(() => {
    if (!match) {
      entryTitleRef.current = null;
      lastSetTitleRef.current = null;
      return;
    }

    if (entryTitleRef.current === null) {
      entryTitleRef.current = document.title;
    }

    return () => {
      if (
        entryTitleRef.current !== null &&
        lastSetTitleRef.current !== null &&
        document.title === lastSetTitleRef.current
      ) {
        document.title = entryTitleRef.current;
      }

      entryTitleRef.current = null;
      lastSetTitleRef.current = null;
    };
  }, [match]);

  useEffect(() => {
    if (!match) {
      return;
    }

    const resolvedTitle = resolvePageTitle(title);

    if (resolvedTitle !== null) {
      document.title = resolvedTitle;
      lastSetTitleRef.current = resolvedTitle;
    }
  }, [match, title]);

  // Return function for dynamic title updates
  return (newTitle: string | null) => {
    if (match) {
      const resolvedTitle = resolvePageTitle(newTitle);

      if (resolvedTitle === null) {
        return;
      }

      document.title = resolvedTitle;
      lastSetTitleRef.current = resolvedTitle;
    }
  };
}
